import { useCallback, useEffect, useRef } from 'react';

/**
 * Опции хука {@link useBlobUrl}.
 */
interface UseBlobUrlOptions {
  /**
   * Режим работы с несколькими URL одновременно.
   *
   * - `false` (по умолчанию) — каждый новый `createUrl()` автоматически
   *   освобождает предыдущий URL. Подходит для случаев с одним изображением.
   * - `true` — `createUrl()` добавляет URL в коллекцию без авто-очистки.
   *   Потребитель сам вызывает `revokeUrl()` перед массовым созданием.
   *
   * @defaultValue `false`
   */
  multiple?: boolean;
}

/**
 * Результат хука {@link useBlobUrl}.
 */
interface UseBlobUrlResult {
  /**
   * Создаёт Blob URL из переданного Blob.
   *
   * В одиночном режиме (`multiple: false`) — автоматически освобождает
   * предыдущий URL перед созданием нового.
   * В множественном режиме (`multiple: true`) — добавляет URL в коллекцию.
   */
  createUrl: (blob: Blob) => string;
  /**
   * Принудительно освобождает все отслеживаемые Blob URL.
   */
  revokeUrl: () => void;
}

/**
 * Хук для безопасного управления Blob URL.
 *
 * Автоматически освобождает память:
 * - при создании нового URL (в одиночном режиме)
 * - при вызове `revokeUrl()`
 * - при размонтировании компонента
 *
 * @example Одиночный режим
 * ```tsx
 * const { createUrl, revokeUrl } = useBlobUrl();
 *
 * // Загрузка фото — createUrl сам освободит предыдущий blob URL
 * loadPhoto().then(blob => setSrc(createUrl(blob)));
 *
 * // Смена на внешний URL
 * revokeUrl();
 * setSrc(externalUrl);
 * ```
 *
 * @example Множественный режим
 * ```tsx
 * const { createUrl, revokeUrl } = useBlobUrl({ multiple: true });
 *
 * // Перед массовой загрузкой — чистим старые URL
 * revokeUrl();
 *
 * // Создаём новые
 * const urls = blobs.map(blob => createUrl(blob));
 * ```
 */
export const useBlobUrl = (
  options: UseBlobUrlOptions = {},
): UseBlobUrlResult => {
  const { multiple = false } = options;
  const urlsRef = useRef<string[]>([]);

  useEffect(() => {
    return () => {
      urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      urlsRef.current = [];
    };
  }, []);

  const createUrl = useCallback(
    (blob: Blob): string => {
      if (!multiple) {
        urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        urlsRef.current = [];
      }

      const objectUrl = URL.createObjectURL(blob);

      urlsRef.current.push(objectUrl);

      return objectUrl;
    },
    [multiple],
  );

  const revokeUrl = useCallback(() => {
    urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    urlsRef.current = [];
  }, []);

  return { createUrl, revokeUrl };
};
